/**
 * Service de collecte et de gestion des diagnostics d'erreur pour HockeyDynasty.
 * Conçu pour être autonome, ultra-robuste et 100% compatible iOS Safari / iPhone.
 */

export interface ErrorDiagnosticData {
  source: 'React Error Boundary' | 'window.onerror' | 'window.onunhandledrejection' | 'Manuel';
  message: string;
  stack?: string;
  componentStack?: string;
  pathname: string;
  href: string;
  userAgent: string;
  viewportWidth: number;
  viewportHeight: number;
  screenWidth: number;
  screenHeight: number;
  devicePixelRatio: number;
  isEmbed: boolean;
  modeText: 'EMBED' | 'NORMAL';
  selectedLeague: { id?: number; name?: string } | null;
  currentLeagueId: number | null;
  timestamp: string;
  isoDate: string;
}

const STORAGE_KEY = 'hd_last_error_diagnostic';

/**
 * Nettoie une chaîne de caractères pour éliminer toute fuite de données sensibles potentielles.
 */
function sanitizeSensitiveText(text: string): string {
  if (!text) return '';
  try {
    return text
      // Masquer les tokens JWT potentiels
      .replace(/eyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]*/g, '[REDACTED_JWT]')
      // Masquer les clés API type supabase anon/service
      .replace(/sbp_[a-zA-Z0-9_]{10,}/g, '[REDACTED_KEY]')
      // Masquer les mots de passe potentiels dans les requêtes
      .replace(/(password|passwd|pwd|secret|token)=[^& \s]+/gi, '$1=[REDACTED]');
  } catch {
    return text;
  }
}

/**
 * Détecte si l'application est en mode EMBED (iframe / paramètre d'URL / storage).
 */
function detectIsEmbed(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    const embedParam = urlParams.get('embed');
    if (embedParam === '1' || embedParam === 'true') return true;

    const sessionEmbed = sessionStorage.getItem('dp_embed_mode');
    if (sessionEmbed === 'true') return true;

    // Détection iframe standard
    return window.self !== window.top;
  } catch {
    return false;
  }
}

/**
 * Tente d'extraire la ligue sélectionnée depuis le localStorage ou l'URL de manière sécurisée.
 */
function extractLeagueInfo(): { selectedLeague: { id?: number; name?: string } | null; currentLeagueId: number | null } {
  let selectedLeague: { id?: number; name?: string } | null = null;
  let currentLeagueId: number | null = null;

  try {
    if (typeof window !== 'undefined') {
      // 1. Lire depuis league-storage (Zustand)
      const rawLeague = localStorage.getItem('league-storage');
      if (rawLeague) {
        const parsed = JSON.parse(rawLeague);
        if (parsed && parsed.state && parsed.state.selectedLeague) {
          selectedLeague = {
            id: parsed.state.selectedLeague.id,
            name: parsed.state.selectedLeague.name
          };
          currentLeagueId = parsed.state.selectedLeague.id ?? null;
        }
      }

      // 2. Fallback via URL si ID présent dans le pathname (ex: /league-payment/123)
      if (!currentLeagueId) {
        const match = window.location.pathname.match(/\/(?:league-payment|other-teams)\/(\d+)/);
        if (match && match[1]) {
          currentLeagueId = parseInt(match[1], 10);
        }
      }
    }
  } catch {
    // Ignorer les erreurs d'extraction
  }

  return { selectedLeague, currentLeagueId };
}

/**
 * Construit un objet de diagnostic complet à partir d'une erreur et de l'environnement actuel.
 */
export function buildDiagnosticData(
  error: any,
  source: ErrorDiagnosticData['source'],
  componentStack?: string
): ErrorDiagnosticData {
  let message = 'Erreur inconnue';
  let stack = '';

  if (typeof error === 'string') {
    message = error;
  } else if (error instanceof Error) {
    message = error.message || error.toString();
    stack = error.stack || '';
  } else if (error && typeof error === 'object') {
    if (error.message) {
      message = String(error.message);
    } else if (error.reason) {
      message = String(error.reason?.message || error.reason);
      stack = error.reason?.stack || '';
    } else {
      try {
        message = JSON.stringify(error);
      } catch {
        message = String(error);
      }
    }
    if (!stack && error.stack) {
      stack = String(error.stack);
    }
  }

  const isEmbed = detectIsEmbed();
  const { selectedLeague, currentLeagueId } = extractLeagueInfo();

  const now = new Date();
  const timestamp = now.toLocaleDateString('fr-CA') + ' ' + now.toLocaleTimeString('fr-CA');
  const isoDate = now.toISOString();

  const diagnostic: ErrorDiagnosticData = {
    source,
    message: sanitizeSensitiveText(message),
    stack: stack ? sanitizeSensitiveText(stack) : undefined,
    componentStack: componentStack ? sanitizeSensitiveText(componentStack) : undefined,
    pathname: typeof window !== 'undefined' ? window.location.pathname : '',
    href: typeof window !== 'undefined' ? sanitizeSensitiveText(window.location.href) : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Inconnu',
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    screenWidth: typeof window !== 'undefined' && window.screen ? window.screen.width : 0,
    screenHeight: typeof window !== 'undefined' && window.screen ? window.screen.height : 0,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
    isEmbed,
    modeText: isEmbed ? 'EMBED' : 'NORMAL',
    selectedLeague,
    currentLeagueId,
    timestamp,
    isoDate
  };

  return diagnostic;
}

/**
 * Sauvegarde le diagnostic dans le sessionStorage pour consultation ultérieure.
 */
export function saveDiagnosticToSession(data: ErrorDiagnosticData): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  } catch (err) {
    console.warn('[ErrorDiagnostic] Impossible d\'écrire dans sessionStorage:', err);
  }
}

/**
 * Récupère le dernier diagnostic sauvegardé depuis le sessionStorage.
 */
export function getLastSavedDiagnostic(): ErrorDiagnosticData | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    }
  } catch {
    // Ignorer
  }
  return null;
}

/**
 * Formate le diagnostic sous forme de texte clair pour la copie ou l'affichage console.
 */
export function formatDiagnosticAsText(data: ErrorDiagnosticData): string {
  const parts = [
    '=== HOCKEYDYNASTY - RAPPORT D\'ERREUR ===',
    `Date & Heure : ${data.timestamp} (${data.isoDate})`,
    `Source : ${data.source}`,
    `Mode : ${data.modeText}`,
    `Page (pathname) : ${data.pathname}`,
    `URL complète : ${data.href}`,
    '',
    '--- ERREUR ---',
    `Message : ${data.message}`,
    data.stack ? `Stack Trace :\n${data.stack}` : '',
    data.componentStack ? `React Component Stack :\n${data.componentStack}` : '',
    '',
    '--- ENVIRONNEMENT APPAREIL ---',
    `Navigateur / User Agent : ${data.userAgent}`,
    `Viewport : ${data.viewportWidth} x ${data.viewportHeight} (DPR: ${data.devicePixelRatio})`,
    `Écran physique : ${data.screenWidth} x ${data.screenHeight}`,
    '',
    '--- CONTEXTE LIGUE ---',
    `Ligue sélectionnée : ${data.selectedLeague ? `${data.selectedLeague.name ?? 'Sans nom'} (ID: ${data.selectedLeague.id ?? 'N/A'})` : 'Aucune'}`,
    `Current League ID : ${data.currentLeagueId !== null ? data.currentLeagueId : 'N/A'}`,
    '========================================='
  ];

  return parts.filter((line) => line !== '').join('\n');
}

/**
 * Enregistre le diagnostic dans la console avec un format lisible.
 */
export function logDiagnosticToConsole(data: ErrorDiagnosticData): void {
  try {
    console.group('%c⚠️ [HockeyDynasty Error Diagnostic]', 'background: #b91c1c; color: #ffffff; font-weight: bold; padding: 4px 8px; border-radius: 4px;');
    console.error('Source:', data.source);
    console.error('Message:', data.message);
    if (data.stack) console.error('Stack:', data.stack);
    if (data.componentStack) console.error('Component Stack:', data.componentStack);
    console.log('Diagnostic complet:', data);
    console.groupEnd();
  } catch {
    console.error('[HockeyDynasty Error Diagnostic]', data.message, data);
  }
}

/**
 * Copie du texte dans le presse-papiers avec fallback universel pour iOS Safari.
 */
export async function copyToClipboardFallback(text: string): Promise<boolean> {
  // 1. Essayer l'API standard Clipboard si disponible
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Échec de l'API moderne, tenter le fallback
    }
  }

  // 2. Fallback robuste iOS/Safari avec un textarea temporaire
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0.01';
    textArea.style.zIndex = '-9999';
    textArea.style.fontSize = '16px'; // Évite le zoom automatique sur iOS

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    // Pour iOS Safari
    const range = document.createRange();
    range.selectNodeContents(textArea);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    textArea.setSelectionRange(0, 999999);

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.warn('[ErrorDiagnostic] Échec de la copie:', err);
    return false;
  }
}
